import './Artist.css';
import html from './Artist.html';
import { getArtistAlbums, getItem } from '../../api/embyApi';
import { createParty, queueItem } from '../../api/party';
import { getActiveMusicPartyId, joinMusicParty } from '../../musicBar/MusicBar';
import type { RouteParams } from '../../router';
import { tileHtml, wireSearchFilter } from '../../tiles';
import { LOADING_HINT } from '../../loading';
import { showToast } from '../../toast';

// Same icon, same "small local constant" convention as musicBar/MusicBar.ts's
// own PLAY_ICON_SVG - not shared from there since neither file otherwise
// depends on the other, and duplicating one SVG string is cheaper than a
// shared icons module for exactly two current call sites (here and
// views/album/Album.ts).
const PLAY_ICON_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 4.5v15l14-7.5Z" fill="currentColor"></path></svg>';

/** Route `/artist/:id` - one artist's albums, reached from a music
 *  library's top-level grid (see views/library) via a MusicArtist tile's
 *  "Voir les albums" link (tiles.ts's DRILL_DOWN table). Structurally the
 *  same "detail header + grid" shape as views/series, just one level up
 *  the music hierarchy (Artist -> Album, not Series -> Season/Episode).
 *  #launchBtn is the one action available at this level itself (every
 *  album's own tracks, in one go) - drilling into a specific album is
 *  still the only way to queue less than the whole discography. */
export async function renderArtist(root: HTMLElement, params: RouteParams): Promise<void> {
  const artistId = params.id as string;
  root.innerHTML = LOADING_HINT;

  let artist;
  let albums;
  try {
    [artist, albums] = await Promise.all([getItem(artistId), getArtistAlbums(artistId)]);
  } catch {
    root.innerHTML = '<p class="error">Impossible de charger cet artiste.</p>';
    return;
  }

  root.innerHTML = html;
  root.querySelector('h1')!.textContent = artist.name;
  (root.querySelector('#artistPhoto') as HTMLImageElement).src = artist.imageUrl;
  root.querySelector('#launchBtnIcon')!.innerHTML = PLAY_ICON_SVG;
  root.querySelector('#back')!.addEventListener('click', () => history.back());

  const launchBtn = root.querySelector('#launchBtn') as HTMLButtonElement;
  const launchBtnLabel = root.querySelector('#launchBtnLabel')!;
  const grid = root.querySelector('#grid')!;
  root.querySelector('#albumCount')!.textContent = `${albums.length} album${albums.length > 1 ? 's' : ''}`;
  if (!albums.length) {
    grid.innerHTML = '<p class="hint">Aucun album trouvé pour cet artiste.</p>';
    root.querySelector('#searchInput')!.remove();
    launchBtn.disabled = true;
    return;
  }
  grid.innerHTML = albums.map(tileHtml).join('');
  wireSearchFilter(root.querySelector('#searchInput') as HTMLInputElement, root, '.tile');

  // Label reflects whether a listen party is already active - same
  // "harmless, occasionally-stale until the next refresh" reasoning as
  // views/album's own equivalent buttons (the click handler below
  // always re-checks getActiveMusicPartyId() fresh regardless).
  const startLabel = 'Écouter toute la discographie';
  const addLabel = 'Ajouter la discographie à la file';
  function refreshLabel(): void {
    if (!launchBtn.disabled) launchBtnLabel.textContent = getActiveMusicPartyId() ? addLabel : startLabel;
  }
  refreshLabel();

  // itemId is the artist itself - the server expands it into every
  // album's tracks, in order, the same mechanism a whole-album launch
  // uses one level down (see views/album/Album.ts, and index.ts's
  // resolveMusicTracksForItem/createMusicPartyFromTracks).
  launchBtn.addEventListener('click', async () => {
    launchBtn.disabled = true;
    const activePartyId = getActiveMusicPartyId();
    try {
      if (activePartyId) {
        launchBtnLabel.textContent = 'Ajout...';
        await queueItem(activePartyId, artistId);
      } else {
        launchBtnLabel.textContent = 'Démarrage...';
        // Same "attach the bar, stay put" pattern as views/album - see
        // its own comment.
        const party = await createParty(artistId);
        joinMusicParty(party.partyId, party);
      }
      launchBtnLabel.textContent = 'Ajouté !';
      setTimeout(() => { launchBtn.disabled = false; refreshLabel(); }, 1500);
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
      launchBtn.disabled = false;
      refreshLabel();
    }
  });
}
